import {
  FORBIDDEN_MEDICAL_TERMS,
  SAFE_FALLBACK_RESPONSES,
  WELLNESS_REPLACEMENTS,
} from '../constants/KILO_LANGUAGE_RULES'
import { stripComplianceBoilerplate } from './complianceDisclaimer'
import { supabase } from './supabaseClient'

export interface ValidationResult {
  isCompliant: boolean
  flaggedTerms: string[]
  sanitisedText: string
  requiresRegeneration: boolean
}

const SINGLE_WORD_FORBIDDEN = new Set([
  'diagnose', 'diagnosis', 'diagnosed', 'diagnostic',
  'syndrome', 'disorder', 'pathology', 'pathological',
  'symptom', 'symptoms', 'symptomatic',
  'clinical', 'clinically', 'subclinical',
  'disease', 'illness', 'condition',
  'deficiency', 'deficient',
  'anaemia', 'anemia',
  'diabetes', 'diabetic',
  'hypertension', 'hypotension',
  'hypothyroid', 'hyperthyroid',
  'depression',
  'treat', 'treatment', 'treating',
  'cure', 'curing', 'curative',
  'therapeutic', 'therapy',
  'prescribe', 'prescription',
  'medicate', 'medication',
  'dose', 'dosage',
  'predisposed', 'predisposition',
])

function termMatches(text: string, term: string): boolean {
  if (SINGLE_WORD_FORBIDDEN.has(term.toLowerCase())) {
    const regex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i')
    return regex.test(text)
  }
  return text.toLowerCase().includes(term.toLowerCase())
}

function replaceTerm(text: string, term: string, replacement: string): string {
  if (SINGLE_WORD_FORBIDDEN.has(term.toLowerCase())) {
    const regex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi')
    return text.replace(regex, replacement)
  }
  const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
  return text.replace(regex, replacement)
}

export function validateAIResponse(text: string): ValidationResult {
  const flaggedTerms: string[] = []

  for (const term of FORBIDDEN_MEDICAL_TERMS) {
    if (termMatches(text, term)) {
      flaggedTerms.push(term)
    }
  }

  let sanitised = text
  let requiresRegeneration = false

  for (const term of flaggedTerms) {
    const replacement = WELLNESS_REPLACEMENTS[term.toLowerCase()]
    if (replacement) {
      sanitised = replaceTerm(sanitised, term, replacement)
    } else {
      requiresRegeneration = true
    }
  }

  sanitised = stripComplianceBoilerplate(sanitised)

  return {
    isCompliant: flaggedTerms.length === 0,
    flaggedTerms,
    sanitisedText: sanitised,
    requiresRegeneration,
  }
}

export function sanitizeCoachText(text: string | null | undefined, context = 'default'): string | null {
  if (!text?.trim()) return null
  const validation = validateAIResponse(text)
  if (validation.requiresRegeneration) {
    return SAFE_FALLBACK_RESPONSES[context] ?? SAFE_FALLBACK_RESPONSES.default
  }
  return validation.sanitisedText
}

export async function logComplianceViolation(
  userId: string,
  originalText: string,
  flaggedTerms: string[],
  context: string,
): Promise<void> {
  if (import.meta.env.DEV) {
    console.warn('[COMPLIANCE] Flagged terms detected:', flaggedTerms)
    return
  }

  await supabase
    .from('ai_compliance_log')
    .insert({
      user_id: userId,
      context,
      flagged_terms: flaggedTerms,
      original_length: originalText.length,
    })
    .then(({ error }) => {
      if (error) console.warn('[COMPLIANCE] Log insert failed:', error.message)
    })
}
